import {
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import React from "react";
import {
  FILTER_KEY_PATTERN,
  SELECT_FILTER_TYPES,
  filterTypeOptions,
} from "../widgetConstants";

/* -------------------------------------------------------------------------- */
// Form rows keep select options as editable text ("value | label" per line)
// and remember whether options are static or loaded from an API source.
export const emptyFilter = {
  key: "",
  label: "",
  type: "text",
  required: false,
  optionsMode: "static",
  optionsText: "",
  source: { url: "", value_field: "id", label_field: "name" },
};

const optionsToText = (options = []) =>
  options
    .map(({ value, label }) =>
      label && label !== value ? `${value} | ${label}` : value
    )
    .join("\n");

const textToOptions = (text) =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [value, ...label] = line.split("|").map((part) => part.trim());
      return { value, label: label.join(" | ") || value };
    });

export const filtersToForm = (filters = []) =>
  filters.map((filter) => ({
    ...emptyFilter,
    key: filter.key || "",
    label: filter.label || "",
    type: filter.type || "text",
    required: !!filter.required,
    optionsMode: filter.source?.url ? "source" : "static",
    optionsText: optionsToText(filter.options),
    source: { ...emptyFilter.source, ...(filter.source || {}) },
  }));

export const formToFilters = (rows) =>
  rows.map((row) => {
    const filter = {
      key: row.key.trim(),
      label: row.label.trim(),
      type: row.type,
      required: row.required,
    };
    if (!SELECT_FILTER_TYPES.includes(row.type)) return filter;
    if (row.optionsMode === "source")
      return {
        ...filter,
        source: {
          url: row.source.url.trim(),
          value_field: row.source.value_field.trim() || "id",
          label_field: row.source.label_field.trim() || "name",
        },
      };
    return { ...filter, options: textToOptions(row.optionsText) };
  });

// Returns { [rowIndex]: { field: message } } for invalid rows only.
export const validateFilters = (rows) =>
  rows.reduce((acc, row, index) => {
    const errors = {};
    const key = row.key.trim();
    if (!key) errors.key = "Key is required";
    else if (!FILTER_KEY_PATTERN.test(key))
      errors.key = "Letters, digits and _ only";
    else if (rows.findIndex((item) => item.key.trim() === key) !== index)
      errors.key = "Duplicate key";
    if (!row.label.trim()) errors.label = "Label is required";
    if (SELECT_FILTER_TYPES.includes(row.type)) {
      if (row.optionsMode === "source" && !row.source.url.trim())
        errors.sourceUrl = "Source URL is required";
      if (
        row.optionsMode === "static" &&
        !textToOptions(row.optionsText).length
      )
        errors.options = "Add at least one option";
    }
    return Object.keys(errors).length ? { ...acc, [index]: errors } : acc;
  }, {});

/* -------------------------------------------------------------------------- */
export const FiltersEditor = ({ value, errors = {}, onChange }) => {
  const updateRow = (index, changes) =>
    onChange(
      value.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...changes } : row
      )
    );
  const removeRow = (index) =>
    onChange(value.filter((row, rowIndex) => rowIndex !== index));

  const fieldProps = (index, errorKey) => ({
    variant: "outlined",
    size: "small",
    fullWidth: true,
    error: !!errors[index]?.[errorKey],
    helperText: errors[index]?.[errorKey],
  });

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} container alignItems="center" justify="space-between">
        <Typography variant="subtitle1">Available Filters</Typography>
        <Button
          size="small"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => onChange([...value, { ...emptyFilter }])}
        >
          Add filter
        </Button>
      </Grid>
      {!value.length && (
        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            The backfill service filters are used as-is. Add a filter here to
            override a service filter with the same key, or to add a new one.
          </Typography>
        </Grid>
      )}
      {value.map((row, index) => {
        const isSelect = SELECT_FILTER_TYPES.includes(row.type);
        return (
          <Grid item xs={12} key={index}>
            <Paper variant="outlined" style={{ padding: 12 }}>
              <Grid container spacing={1} alignItems="flex-start">
                <Grid item xs={4}>
                  <TextField
                    label="Key *"
                    value={row.key}
                    onChange={(e) => updateRow(index, { key: e.target.value })}
                    {...fieldProps(index, "key")}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Label *"
                    value={row.label}
                    onChange={(e) =>
                      updateRow(index, { label: e.target.value })
                    }
                    {...fieldProps(index, "label")}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    select
                    label="Type"
                    value={row.type}
                    onChange={(e) => updateRow(index, { type: e.target.value })}
                    {...fieldProps(index, "type")}
                  >
                    {filterTypeOptions.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={1}>
                  <IconButton size="small" onClick={() => removeRow(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
                <Grid item xs={isSelect ? 4 : 12}>
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        size="small"
                        checked={row.required}
                        onChange={(e) =>
                          updateRow(index, { required: e.target.checked })
                        }
                      />
                    }
                    label="Required"
                  />
                </Grid>
                {isSelect && (
                  <Grid item xs={8}>
                    <TextField
                      select
                      label="Options"
                      value={row.optionsMode}
                      onChange={(e) =>
                        updateRow(index, { optionsMode: e.target.value })
                      }
                      {...fieldProps(index, "optionsMode")}
                    >
                      <MenuItem value="static">Static list</MenuItem>
                      <MenuItem value="source">Load from API</MenuItem>
                    </TextField>
                  </Grid>
                )}
                {isSelect && row.optionsMode === "static" && (
                  <Grid item xs={12}>
                    <TextField
                      label="Options (one per line: value | label)"
                      multiline
                      rows={3}
                      value={row.optionsText}
                      onChange={(e) =>
                        updateRow(index, { optionsText: e.target.value })
                      }
                      {...fieldProps(index, "options")}
                    />
                  </Grid>
                )}
                {isSelect && row.optionsMode === "source" && (
                  <>
                    <Grid item xs={6}>
                      <TextField
                        label="Source URL *"
                        placeholder="/v2/aircrafts"
                        value={row.source.url}
                        onChange={(e) =>
                          updateRow(index, {
                            source: { ...row.source, url: e.target.value },
                          })
                        }
                        {...fieldProps(index, "sourceUrl")}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        label="Value field"
                        value={row.source.value_field}
                        onChange={(e) =>
                          updateRow(index, {
                            source: {
                              ...row.source,
                              value_field: e.target.value,
                            },
                          })
                        }
                        {...fieldProps(index, "valueField")}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        label="Label field"
                        value={row.source.label_field}
                        onChange={(e) =>
                          updateRow(index, {
                            source: {
                              ...row.source,
                              label_field: e.target.value,
                            },
                          })
                        }
                        {...fieldProps(index, "labelField")}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );
};
